import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Resolve,
  Router,
  RouterStateSnapshot
} from '@angular/router';
import { FeatureService } from '@shared/services/feature.service';
import { get, isEmpty } from 'lodash';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';

/** Uses activationFeatures data property to verify features */
@Injectable({
  providedIn: 'root'
})
export class FeatureGuard implements CanActivate, Resolve<{ [id: string]: boolean }> {
  constructor(private readonly featureService: FeatureService, private readonly router: Router) {}

  /**
   * For the router.navigate to home see https://github.com/angular/angular/issues/16211
   * without it navigates back to root but doesn't load the landing page.
   *
   * activationFeatures: string[]
   */
  public canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    if (isEmpty(get(route, 'data.activationFeatures', []))) {
      console.warn('Route activationFeatures empty or not found');

      return of(false).pipe(tap(async () => this.router.navigate(['/'])));
    }

    return this.featureService.getFeatureToggles(route.data.activationFeatures).pipe(
      map((features: { [id: string]: boolean }) =>
        Object.values(features).every((v: boolean) => v)
      ),
      tap((featureEnabled) => {
        if (!featureEnabled) {
          this.router.navigate(['/']);
        }
      })
    );
  }

  /**
   * Resolve feature toggles before loading the page
   *
   * resolveFeatures: string[]
   */
  public resolve(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<{ [id: string]: boolean }> {
    window.debug('FeatureGuard', 'FeatureGuard resolving: ', route.data.resolveFeatures);
    if (!get(route, 'data.resolveFeatures')) {
      return of({});
    }

    return this.featureService.getFeatureToggles(route.data.resolveFeatures);
  }
}
